<template>
  <bb-modal visible @close="onClose()" flavor="card" color="white" publicSize="m" fullMobile class="transfer-details">
    <template v-if="loading">
      <div class="transfer-details__loader">
        <bb-spinner/>
      </div>
    </template>
    <template v-else>
      <div slot="heading" class="transfer-details__title">{{ mergedTranslations.title }}</div>
      <template v-if="showTransferOptions">
        <bb-radio
          class="m-b-10"
          :options="transferOptions"
          v-model="selectedOption"
          :label="mergedTranslations.transfer.title"
          name="transfer-options-radio"/>
      </template>

      <div v-if="isExternalAccount"
        class="transfer-details__content">
        <template v-if="showAmount">
          <div class="transfer-details__amount color-navy">{{ amount }}</div>
          <div class="transfer-details__amount-subtitle">{{ mergedTranslations.sumSubtitle }}</div>
        </template>
        <slot/>
        <div class="transfer-details__subtitle">{{ mergedTranslations.subtitle }}</div>
        <bb-box thin class="transfer-details_details-box">
          <list-item :title="mergedTranslations.recipient" :titleUppercase="false" :titleBold="true" :value="name" name="beneficary-name"/>
          <list-item v-if="!isChannelSE" :title="mergedTranslations.iban" :value="iban" name="iban"/>
          <list-item :title="mergedTranslations.bic" :value="bic" name="bic"/>
          <list-item :title="mergedTranslations.reference" :value="reference" name="reference-number"/>
        </bb-box>
      </div>

      <div v-else :style="`min-height: ${accountSelectionHeight}px`">
        <account-dropdown
          v-if="selectedIban"
          class="m-t-20"
          :selected-iban="selectedIban"
          @onAccountChange="onAccountChange"/>
        <bb-banner permanent type="info" visible class="m-t-20">
          {{ mergedTranslations.transfer.notification }}
        </bb-banner>
      </div>
    </template>
    <template #footer>
      <bb-ticket-stub class="nm-x-20 nm-y-md-20" v-if="isExternalAccount">
        <bb-button
          data-testid="transfer-details-close"
          display="ticket"
          color="white"
          class="color-green no-padding"
          @click="onClose()">
          <span class="color-green"><translate>Got it</translate></span>
        </bb-button>
      </bb-ticket-stub>
      <bb-button
        v-else
        data-testid="transfer-details-payment"
        display="block"
        corner="sharpMobileOnly"
        class="no-padding"
        @click="proceedToPayment">
       {{ mergedTranslations.transfer.button }}
      </bb-button>
    </template>
  </bb-modal>
</template>

<script>
import { formatMoneyWithCurrency } from '@/plugins/numformat'
import { mapState } from 'pinia'
import currencyDefinitions from '../utils/getCurrency'
import ListItem from '@/components/ListItem.vue'
import { useRootStore } from '@/store/root'
import AccountDropdown from '@account/components/AccountDropdown.vue'
import { useAccountStore } from '@account/store/accountStore'
import { AccountRouteName } from '@account/const'

const TransferOption = {
  CURRENT_ACCOUNT: 'current_account',
  EXTERNAL_ACCOUNT: 'external_account'
}
export default {
  components: { AccountDropdown, ListItem },
  name: 'transfer-details',
  props: {
    transferDetails: {
      required: true
    },
    translations: {
      required: false,
      default: () => ({})
    },
    loading: {
      required: false,
      default: false
    },
    hideSum: {
      required: false,
      default: false
    },
    showTransferOptions: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      selectedOption: TransferOption.EXTERNAL_ACCOUNT,
      selectedIban: null
    }
  },
  computed: {
    ...mapState(useRootStore, ['locale', 'isChannelSE']),
    ...mapState(useAccountStore, ['paymentAccounts']),
    accountSelectionHeight  () {
      return this.paymentAccounts.length * 56 + 76 + 20 // 76 is the height of the header, 20 is padding
    },
    amount () {
      return formatMoneyWithCurrency(this.transferDetails.amount, this.transferCurrency, this.locale)
    },
    name () {
      return this.transferDetails ? this.transferDetails.receiverName : '-'
    },
    iban () {
      return this.transferDetails ? this.transferDetails.iban : '-'
    },
    reference () {
      return this.transferDetails ? this.transferDetails.referenceNumber : '-'
    },
    bic () {
      return this.transferDetails ? this.transferDetails.bic : '-'
    },
    transferCurrency () {
      return currencyDefinitions(this.transferDetails.currencyCode)
    },
    mergedTranslations () {
      return {
        iban: this.$gettext('IBAN'),
        bic: this.$gettext('BIC'),
        reference: this.$gettext('Ref number'),
        title: this.$gettext('Waiting for your transfer.'),
        subtitle: this.$gettext('Payment details:'),
        sumSubtitle: this.$gettext('Sum to be transferred'),
        recipient: this.$gettext('Recipient'),
        transfer: {
          title: this.$pgettext('SD_add_funds_modal', 'Choose a way to add funds to your deposit'),
          [TransferOption.CURRENT_ACCOUNT]: this.$pgettext('SD_add_funds_modal', 'Bigbank account '),
          [TransferOption.EXTERNAL_ACCOUNT]: this.$pgettext('SD_add_funds_modal', 'External account'),
          notification: this.$pgettext('SD_add_funds_modal', 'You will be able to review and adjust all payment details in the next step'),
          button: this.$pgettext('SD_add_funds_modal', 'Proceed to payment'),
          paymentDescription: this.$pgettext('SD_add_funds_modal', 'Savings deposit')
        },
        ...this.translations
      }
    },
    showAmount () {
      return !this.hideSum && ![undefined, null, ''].includes(this.transferDetails.amount)
    },
    isExternalAccount () {
      return this.selectedOption === TransferOption.EXTERNAL_ACCOUNT
    },
    transferOptions () {
      return Object.values(TransferOption).map((option) => {
        return {
          value: option,
          text: this.mergedTranslations.transfer[option]
        }
      })
    }
  },
  watch: {
    showTransferOptions (value) {
      if (value) {
        this.selectedOption = TransferOption.CURRENT_ACCOUNT
        this.selectedIban = this.paymentAccounts[0]?.iban || null
      }
    }
  },
  methods: {
    onClose () {
      this.$emit('close')
    },
    onAccountChange (account) {
      this.selectedIban = account.iban
    },
    proceedToPayment () {
      const query = {
        fromIban: this.selectedIban,
        recipientIban: this.iban,
        recipientName: this.name,
        referenceNumber: this.reference,
        description: this.mergedTranslations.transfer.paymentDescription
      }
      this.$router.push({ name: AccountRouteName.OutgoingPayments, query })
    }
  }
}
</script>

<style lang="scss" scoped>
.transfer-details {
  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    color: $blue;
    font-size: $font-size-small;
    font-family: $gotham-medium;
    text-align: center;
  }

  &__subtitle {
    color: $gray;
    text-align: center;
    font-size: $font-size-small;
    line-height: $font-size-small;
    font-family: $gotham-medium;
    margin-top: 15px;
    margin-bottom: 15px;
  }

  &__details-box {
    margin-top: 30px;
  }

  &__amount {
    margin-top: 15px;
    text-align: center;
    font-family: $gotham-medium;
    font-size: $h3-size;
    line-height: $h3-size;
  }

  &__amount-subtitle {
    color: $gray-70;
    font-family: $gotham-bold;
    text-align: center;
    font-size: $font-size-tiny;
    text-transform: uppercase;
    padding-bottom: 15px;
  }
}
</style>
